// ── API CLIENT + IDs (fp-nofodesk-9) — extracted from App.js ──
import { queueSync } from "./offlineQueue";

const API = process.env.REACT_APP_API_URL || "https://api.nofodesk.com/api";

// The session lives in an httpOnly cookie the browser sends by itself; page
// script never sees it. Only a non-secret "signed in" flag is kept here.
// A token saved by the pre-cookie app is still sent until App.js swaps it for
// the cookie (POST /auth/session) and deletes it.
const TOKEN_KEY     = "grantos_auth_token";   // pre-cookie sessions only
const SIGNED_IN_KEY = "nofodesk_signed_in";
const legacyToken = () => localStorage.getItem(TOKEN_KEY);
// Truthy while signed in; callers only use it as a yes/no.
const getToken = () => legacyToken() || (localStorage.getItem(SIGNED_IN_KEY) ? "session" : null);
const setToken = (signedIn) => {
  localStorage.removeItem(TOKEN_KEY);
  if (signedIn) localStorage.setItem(SIGNED_IN_KEY, "1");
  else localStorage.removeItem(SIGNED_IN_KEY);
};

function apiFetch(url, options = {}) {
  const token = legacyToken();
  const headers = { "X-Requested-With": "XMLHttpRequest", ...(options.headers || {}) };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const wsOwner = localStorage.getItem('gos_ws_owner');
  if (wsOwner) headers["X-Workspace-Owner"] = wsOwner;
  return fetch(url, { ...options, credentials: "include", headers });
}

function apiSyncTable(endpoint, records) {
  if (!getToken()) return;
  if (!navigator.onLine) { queueSync(endpoint, records); return; }
  apiFetch(`${API}/db/${endpoint}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ records }),
  }).then(r => { if (r.status === 403) console.warn('[NofoDesk] sync blocked — view-only workspace'); })
    .catch(() => { queueSync(endpoint, records); });
}

function persistRecordsDiff(table, prevRecords, nextRecords, canEdit) {
  if (!canEdit || !getToken()) return;
  if (!navigator.onLine) { queueSync(table, nextRecords); return; }

  const prevMap = new Map((prevRecords || []).map(r => [String(r.id), r]));
  const nextMap = new Map((nextRecords || []).map(r => [String(r.id), r]));

  for (const id of prevMap.keys()) {
    if (!nextMap.has(id)) {
      apiFetch(`${API}/db/${table}/${id}`, { method: 'DELETE' })
        .then(r => { if (r.status === 403) console.warn('[NofoDesk] delete blocked — view-only workspace'); })
        .catch(() => { queueSync(table, nextRecords); });
    }
  }

  for (const [id, rec] of nextMap) {
    if (!prevMap.has(id)) {
      apiFetch(`${API}/db/${table}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ record: rec }),
      }).then(r => { if (r.status === 403) console.warn('[NofoDesk] create blocked — view-only workspace'); })
        .catch(() => { queueSync(table, nextRecords); });
    } else {
      const prev = prevMap.get(id);
      if (JSON.stringify(prev) !== JSON.stringify(rec)) {
        apiFetch(`${API}/db/${table}/${id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ record: rec }),
        }).then(r => { if (r.status === 403) console.warn('[NofoDesk] update blocked — view-only workspace'); })
          .catch(() => { queueSync(table, nextRecords); });
      }
    }
  }
}

async function apiLoadTable(endpoint) {
  if (!getToken()) return null;
  try {
    const res = await apiFetch(`${API}/db/${endpoint}`);
    if (!res.ok) return null;
    const json = await res.json();
    return Array.isArray(json.records) && json.records.length > 0 ? json.records : null;
  } catch { return null; }
}
// ── IDs ───────────────────────────────────────────────────────
function generateId() { return Math.random().toString(36).substring(2, 15); }
function computeGrantId(grantData) {
  const raw = grantData.opportunityIdentification?.callReference || grantData.opportunityIdentification?.callTitle || "unknown";
  return raw.slice(0, 40).replace(/[^a-z0-9]/gi, "-").toLowerCase();
}

export { API, TOKEN_KEY, SIGNED_IN_KEY, legacyToken, getToken, setToken, apiFetch, apiSyncTable, persistRecordsDiff, apiLoadTable, generateId, computeGrantId };
